import { ImpersonationStopRow } from "@/components/layout/ImpersonationStopRow";
import { DashboardBreadcrumbs } from "@/components/layout/DashboardBreadcrumbs";
import { UserMenuButton } from "@/features/invitee/components/UserMenuButton";

interface PageHeaderProps {
  title?: string;
  /** Override default title sizing (e.g. Help & About) */
  titleClassName?: string;
  showProfile?: boolean;
  /**
   * Root `data-testid` for the header bar; child ids use this prefix (e.g. `${testIdRoot}-title`).
   * Default matches legacy invitee list: `invitee-page-header`.
   */
  testIdRoot?: string;
  /** `data-testid` on the compact stop-impersonate control (default: dashboard strip id). */
  impersonationStopDataTestId?: string;
  /** Same redirect fallback as StopImpersonateButton for applicant-style sessions. */
  useApplicantImpersonationFallback?: boolean;
  /** Breadcrumb row directly under the top strip (default true). */
  showBreadcrumbs?: boolean;
}

export function PageHeader({
  title = "INVITES",
  titleClassName,
  showProfile = true,
  testIdRoot = "invitee-page-header",
  impersonationStopDataTestId = "dashboard-page-header-stop-impersonate",
  useApplicantImpersonationFallback = false,
  showBreadcrumbs = true,
}: PageHeaderProps) {
  return (
    <>
      <div
        data-testid={testIdRoot}
        className="relative z-10 flex h-[3.2rem] min-h-[3.2rem] max-h-[3.2rem] w-full flex-row items-center justify-between gap-3 border-b border-[rgb(21_151_255)]/20 bg-[rgb(21_151_255)]/[0.06] px-3 shadow-sm sm:px-6"
      >
        <div
          data-testid={`${testIdRoot}-title-block`}
          className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3"
        >
          <div
            data-testid={`${testIdRoot}-icon`}
            className="shrink-0 rounded-lg border border-[rgb(21_151_255)]/20 bg-white p-1 text-[rgb(21_151_255)] shadow-sm sm:p-1.5"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <rect width="18" height="18" x="3" y="3" rx="2" />
              <path d="M3 9h18" />
              <path d="M9 21V9" />
            </svg>
          </div>
          <div className="flex min-w-0 flex-col justify-center">
            <h1
              data-testid={`${testIdRoot}-title`}
              className={
                titleClassName ??
                "text-base sm:text-lg font-bold text-slate-900 tracking-tight leading-none"
              }
            >
              {title}
            </h1>
          </div>
        </div>

        <div
          data-testid={`${testIdRoot}-actions`}
          className="flex shrink-0 items-center gap-2 sm:gap-3"
        >
          <ImpersonationStopRow
            variant="pageHeader"
            dataTestId={impersonationStopDataTestId}
            useApplicantImpersonationFallback={useApplicantImpersonationFallback}
          />
          {showProfile && (
            <div
              data-testid={`${testIdRoot}-profile-wrap`}
              className="flex shrink-0 items-center sm:border-l sm:border-[rgb(21_151_255)]/20 sm:pl-4"
            >
              <UserMenuButton testIdPrefix={testIdRoot} />
            </div>
          )}
        </div>
      </div>

      {showBreadcrumbs ? <DashboardBreadcrumbs /> : null}
    </>
  );
}
